// Detalhe do lead — /crm/leads/:id (Story 11.3, fecha o "lead loop").
//
// Reúso máximo (esta story é majoritariamente frontend):
//  - GET/PUT/discard via hooks de 11.2 (useCrmLead / useUpdateCrmLead / useDiscardCrmLead).
//  - <ActivityTimeline target={{kind:'lead',id}}/> de 11.8 — self-contained (fetch + form inline).
//  - <LeadForm> de 11.2 em modo edição (injeta version no submit pro optimistic lock).
//  - <DiscardLeadDialog> de 11.2.
//
// AC1 header com badges + alerta "Contato vencido"; AC2 timeline; AC3/AC4 deals e
// propostas (placeholder vazio — entidades chegam em 11.4/11.6, só não quebrar);
// AC6 editar; AC7 descartar; AC8 RBAC; AC9 skeleton; AC10 breadcrumb + voltar + title.

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Briefcase,
  ExternalLink,
  FileText,
  Pencil,
  RefreshCw,
  Trash2,
  TriangleAlert,
} from 'lucide-react';
import { toast } from 'sonner';

import {
  formatBR,
  todayLocal,
  type CreateCrmLeadDto,
  type CrmLeadOriginSchema,
  type CrmLeadPotentialSchema,
  type CrmLeadStatusSchema,
  type CrmLeadTemperatureSchema,
  type UpdateCrmLeadDto,
} from '@control-center/shared';

import { PillButton } from '../../../components/clients/PillButton';
import { ActivityTimeline } from '../../../components/crm/ActivityTimeline';
import { DiscardLeadDialog } from '../../../components/crm/DiscardLeadDialog';
import { LeadForm } from '../../../components/crm/LeadForm';
import { useCrmLead, useDiscardCrmLead, useUpdateCrmLead } from '../../../hooks/use-crm-leads';
import { useSaasProducts } from '../../../hooks/use-saas-products';
import { useUsers } from '../../../hooks/use-users';
import { useCrmPermissions } from '../../../hooks/use-crm-permissions';
import { extractErrorMessage } from '../../../lib/errors';
import type { CrmLeadDetailEntity } from '../../../lib/crm-leads-api';

const TEMPERATURE_BADGE: Record<CrmLeadTemperatureSchema, string> = {
  quente: 'border-bad/40 text-bad',
  morno: 'border-warn/40 text-warn',
  frio: 'border-line text-muted',
};

const TEMPERATURE_LABEL: Record<CrmLeadTemperatureSchema, string> = {
  quente: 'Quente',
  morno: 'Morno',
  frio: 'Frio',
};

const STATUS_LABEL: Record<CrmLeadStatusSchema, string> = {
  novo: 'Novo',
  qualificando: 'Qualificando',
  qualificado: 'Qualificado',
  convertido: 'Convertido',
  descartado: 'Descartado',
};

const POTENTIAL_LABEL: Record<CrmLeadPotentialSchema, string> = {
  low: 'Baixo',
  medium: 'Médio',
  high: 'Alto',
};

const ORIGIN_LABEL: Record<CrmLeadOriginSchema, string> = {
  instagram: 'Instagram',
  whatsapp: 'WhatsApp',
  indicacao: 'Indicação',
  trafego_pago: 'Tráfego pago',
  site: 'Site',
  landing_page: 'Landing page',
  evento: 'Evento',
  prospeccao_ativa: 'Prospecção ativa',
  outro: 'Outro',
};

/** Contato vencido: próximo contato <= hoje e lead ainda ativo (AC1). */
function isContactOverdue(lead: CrmLeadDetailEntity): boolean {
  if (!lead.next_contact_date) return false;
  if (lead.status === 'convertido' || lead.status === 'descartado') return false;
  return lead.next_contact_date <= todayLocal();
}

export function CrmLeadDetailPage(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { canWrite } = useCrmPermissions();

  const { data: lead, isLoading, isError, error, refetch, isFetching } = useCrmLead(id);
  const { data: saasProducts } = useSaasProducts({ limit: 100 });
  const { data: responsibleUsers } = useUsers();

  const [isEditing, setIsEditing] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);

  const update = useUpdateCrmLead();
  const discard = useDiscardCrmLead();

  // AC10 — título da aba.
  useEffect(() => {
    if (lead) document.title = `${lead.name} — Leads | Control Center`;
    return () => {
      document.title = 'Control Center';
    };
  }, [lead]);

  // Lead convertido não é editável (AC6 / regra de negócio do backend).
  const isLocked = lead?.status === 'convertido' || lead?.status === 'descartado';

  if (isLoading) {
    return <LeadDetailSkeleton />;
  }

  if (isError || !lead) {
    return (
      <NotFoundState
        message={extractErrorMessage(error, 'Esse lead não existe — ou foi removido.')}
        onBack={() => navigate('/crm/leads')}
      />
    );
  }

  const overdue = isContactOverdue(lead);

  const handleUpdate = async (values: CreateCrmLeadDto): Promise<void> => {
    try {
      const payload: UpdateCrmLeadDto = { ...values, version: lead.version };
      await update.mutateAsync({ id: lead.id, payload });
      toast.success('Lead atualizado.', { description: lead.name });
      setIsEditing(false);
    } catch (err) {
      toast.error('Não conseguimos salvar a edição.', {
        description: extractErrorMessage(err, 'Tente de novo em instantes.'),
      });
    }
  };

  const handleDiscard = (reason: string): void => {
    discard.mutate(
      { id: lead.id, payload: { discard_reason: reason } },
      {
        onSuccess: (updated) => {
          toast.success('Lead descartado.', { description: updated.name });
          setDiscardOpen(false);
        },
        onError: (err) => {
          toast.error('Não conseguimos descartar.', {
            description: extractErrorMessage(err, 'Tente de novo em instantes.'),
          });
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-5 pb-10">
      {/* Breadcrumb + voltar (AC10) */}
      <nav aria-label="Navegação" className="flex items-center gap-1 text-[12px] text-muted-2">
        <button
          type="button"
          onClick={() => navigate('/crm/leads')}
          className="inline-flex items-center gap-1 transition-colors hover:text-ink"
        >
          <ArrowLeft size={13} strokeWidth={2} />
          CRM
        </button>
        <span aria-hidden>/</span>
        <button
          type="button"
          onClick={() => navigate('/crm/leads')}
          className="transition-colors hover:text-ink"
        >
          Leads
        </button>
        <span aria-hidden>/</span>
        <span className="truncate text-ink-2">{lead.name}</span>
      </nav>

      {/* Header com badges (AC1) */}
      <header className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="flex flex-col gap-2">
          <h1 className="m-0 text-[28px] font-bold leading-none tracking-[-0.025em] text-ink">
            {lead.name}
          </h1>
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex items-center rounded-[6px] border px-2 py-[2px] text-[11px] ${TEMPERATURE_BADGE[lead.temperature]}`}
            >
              {TEMPERATURE_LABEL[lead.temperature]}
            </span>
            <span className="inline-flex items-center rounded-[6px] border border-line px-2 py-[2px] text-[11px] text-muted-2">
              {STATUS_LABEL[lead.status]}
            </span>
            {lead.company && <span className="text-[12px] text-muted">· {lead.company}</span>}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <PillButton
            onClick={() => refetch()}
            icon={
              <RefreshCw
                size={14}
                strokeWidth={2}
                className={isFetching ? 'animate-spin' : undefined}
              />
            }
          >
            Atualizar
          </PillButton>
          {canWrite && !isLocked && !isEditing && (
            <>
              <PillButton
                solid
                onClick={() => setIsEditing(true)}
                icon={<Pencil size={14} strokeWidth={2} />}
              >
                Editar
              </PillButton>
              <PillButton
                onClick={() => setDiscardOpen(true)}
                icon={<Trash2 size={14} strokeWidth={2} />}
              >
                Descartar
              </PillButton>
            </>
          )}
        </div>
      </header>

      {/* Alerta de contato vencido (AC1) */}
      {overdue && (
        <div
          role="status"
          className="flex items-start gap-2 rounded-[12px] border border-warn/40 bg-warn/5 p-4 text-[12px] text-ink-2"
        >
          <TriangleAlert
            size={15}
            strokeWidth={2}
            className="mt-[1px] shrink-0 text-warn"
            aria-hidden
          />
          <div>
            <span className="font-semibold text-ink">Contato vencido.</span>{' '}
            {lead.next_action ? `${lead.next_action} — ` : ''}
            estava previsto pra {formatBR(lead.next_contact_date as string)}.
          </div>
        </div>
      )}

      {/* Lead convertido: link pro cliente gerado (AC6) */}
      {lead.status === 'convertido' && lead.client && (
        <div className="flex items-center gap-2 rounded-[12px] border border-ok/30 bg-ok/5 p-4 text-[12px] text-ink-2">
          <span className="font-semibold text-ink">Lead convertido.</span>
          <button
            type="button"
            onClick={() => navigate(`/identities/${lead.client?.id}`)}
            className="inline-flex items-center gap-1 text-ink transition-colors hover:text-ok"
          >
            Ver cliente {lead.client.name}
            <ExternalLink size={12} strokeWidth={2} />
          </button>
        </div>
      )}

      {isEditing ? (
        <EditPanel
          lead={lead}
          saasProducts={saasProducts?.items ?? []}
          responsibleUsers={responsibleUsers ?? []}
          isPending={update.isPending}
          onSubmit={handleUpdate}
          onCancel={() => setIsEditing(false)}
        />
      ) : (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
          {/* Coluna esquerda: dados + relações + deals + propostas */}
          <div className="flex flex-col gap-5">
            <LeadInfoCard lead={lead} />
            <LeadDealsSection />
            <LeadProposalsSection />
          </div>

          {/* Coluna direita: timeline (AC2) — componente self-contained de 11.8 */}
          <section className="flex flex-col gap-3">
            <h2 className="text-[15px] font-semibold tracking-[-0.005em] text-ink">Atividades</h2>
            <ActivityTimeline target={{ kind: 'lead', id: lead.id }} canWrite={canWrite} />
          </section>
        </div>
      )}

      {discardOpen && (
        <DiscardLeadDialog
          open={discardOpen}
          onOpenChange={setDiscardOpen}
          leadName={lead.name}
          isPending={discard.isPending}
          onConfirm={handleDiscard}
        />
      )}
    </div>
  );
}

// =============================================================================
// Edição (AC6) — reusa LeadForm, injeta version no submit (optimistic lock)
// =============================================================================

interface EditPanelProps {
  lead: CrmLeadDetailEntity;
  saasProducts: { id: string; name: string }[];
  responsibleUsers: { id: string; name: string }[];
  isPending: boolean;
  onSubmit: (values: CreateCrmLeadDto) => void;
  onCancel: () => void;
}

function EditPanel({
  lead,
  saasProducts,
  responsibleUsers,
  isPending,
  onSubmit,
  onCancel,
}: EditPanelProps): JSX.Element {
  // Pré-preenche o form com os campos editáveis do lead. O picker de responsável
  // (Story 11.4) substituiu o campo solto — agora editável via select de usuários.
  const initialValues = {
    name: lead.name,
    company: lead.company,
    segment: lead.segment,
    phone: lead.phone,
    email: lead.email,
    origin: lead.origin,
    saas_product_id: lead.saas_product_id,
    pain_point: lead.pain_point,
    potential: lead.potential,
    temperature: lead.temperature,
    responsible_user_id: lead.responsible_user_id,
    next_action: lead.next_action,
    next_contact_date: lead.next_contact_date,
    observations: lead.observations,
  };

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-4">
      <h2 className="text-[15px] font-semibold tracking-[-0.005em] text-ink">Editar lead</h2>
      <LeadForm
        initialValues={initialValues}
        saasProducts={saasProducts}
        responsibleUsers={responsibleUsers}
        isPending={isPending}
        submitLabel="Salvar alterações"
        onSubmit={onSubmit}
        onCancel={onCancel}
      />
    </div>
  );
}

// =============================================================================
// Card de dados do lead
// =============================================================================

function LeadInfoCard({ lead }: { lead: CrmLeadDetailEntity }): JSX.Element {
  const rows: { label: string; value: string }[] = [
    { label: 'Empresa', value: lead.company ?? '—' },
    { label: 'Segmento', value: lead.segment ?? '—' },
    { label: 'Telefone', value: lead.phone ?? '—' },
    { label: 'E-mail', value: lead.email ?? '—' },
    { label: 'Origem', value: ORIGIN_LABEL[lead.origin] },
    { label: 'SaaS de interesse', value: lead.saas_product?.name ?? '—' },
    { label: 'Potencial', value: POTENTIAL_LABEL[lead.potential] },
    { label: 'Responsável', value: lead.responsible_user?.name ?? '—' },
    { label: 'Próxima ação', value: lead.next_action ?? '—' },
    {
      label: 'Próximo contato',
      value: lead.next_contact_date ? formatBR(lead.next_contact_date) : '—',
    },
  ];

  return (
    <section className="rounded-[14px] border border-line bg-panel p-[20px]">
      <h2 className="mb-4 text-[13px] font-semibold tracking-[-0.005em] text-ink">Dados do lead</h2>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
        {rows.map((row) => (
          <div key={row.label} className="flex flex-col gap-[2px]">
            <dt className="text-[11px] uppercase tracking-wider text-muted-3">{row.label}</dt>
            <dd className="m-0 text-[13px] text-ink">{row.value}</dd>
          </div>
        ))}
      </dl>

      {lead.pain_point && (
        <div className="mt-4 border-t border-line/60 pt-4">
          <p className="text-[11px] uppercase tracking-wider text-muted-3">Dor / necessidade</p>
          <p className="mt-1 whitespace-pre-line text-[13px] leading-relaxed text-ink-2">
            {lead.pain_point}
          </p>
        </div>
      )}

      {lead.observations && (
        <div className="mt-4 border-t border-line/60 pt-4">
          <p className="text-[11px] uppercase tracking-wider text-muted-3">Observações</p>
          <p className="mt-1 whitespace-pre-line text-[13px] leading-relaxed text-ink-2">
            {lead.observations}
          </p>
        </div>
      )}
    </section>
  );
}

// =============================================================================
// Seções de Deals (AC3) e Propostas (AC4) — placeholders até 11.4/11.6
// =============================================================================

function LeadDealsSection(): JSX.Element {
  return (
    <section className="rounded-[14px] border border-line bg-panel p-[20px]">
      <h2 className="mb-3 flex items-center gap-2 text-[13px] font-semibold tracking-[-0.005em] text-ink">
        <Briefcase size={14} strokeWidth={2} className="text-muted-2" aria-hidden />
        Negociações
      </h2>
      <div className="rounded-[12px] border border-dashed border-line bg-page py-6 text-center">
        <p className="text-[12px] text-muted">Nenhuma negociação registrada.</p>
        <p className="mx-auto mt-1 max-w-[280px] text-[11px] text-muted-3">
          As negociações deste lead aparecem aqui quando o módulo de deals entrar no ar.
        </p>
      </div>
    </section>
  );
}

function LeadProposalsSection(): JSX.Element {
  return (
    <section className="rounded-[14px] border border-line bg-panel p-[20px]">
      <h2 className="mb-3 flex items-center gap-2 text-[13px] font-semibold tracking-[-0.005em] text-ink">
        <FileText size={14} strokeWidth={2} className="text-muted-2" aria-hidden />
        Propostas
      </h2>
      <div className="rounded-[12px] border border-dashed border-line bg-page py-6 text-center">
        <p className="text-[12px] text-muted">Nenhuma proposta.</p>
        <p className="mx-auto mt-1 max-w-[280px] text-[11px] text-muted-3">
          As propostas geradas pra este lead aparecem aqui quando o módulo de propostas entrar no
          ar.
        </p>
      </div>
    </section>
  );
}

// =============================================================================
// States (AC9 skeleton / 404)
// =============================================================================

function LeadDetailSkeleton(): JSX.Element {
  return (
    <div className="flex flex-col gap-5">
      <div className="h-3 w-40 animate-pulse rounded bg-panel-2" />
      <div className="flex flex-col gap-2">
        <div className="h-7 w-64 animate-pulse rounded bg-panel-2" />
        <div className="h-4 w-32 animate-pulse rounded bg-panel-2" />
      </div>
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <div className="h-64 animate-pulse rounded-[14px] bg-panel-2" />
        <div className="h-64 animate-pulse rounded-[14px] bg-panel-2" />
      </div>
    </div>
  );
}

function NotFoundState({ message, onBack }: { message: string; onBack: () => void }): JSX.Element {
  return (
    <div className="rounded-[14px] border border-line bg-panel p-8 text-center">
      <h1 className="text-[16px] font-semibold tracking-[-0.005em] text-ink">
        Lead não encontrado
      </h1>
      <p className="mx-auto mt-1 max-w-[360px] text-[12px] text-muted">{message}</p>
      <div className="mt-4 flex justify-center">
        <PillButton onClick={onBack} icon={<ArrowLeft size={14} strokeWidth={2} />}>
          Voltar para a lista
        </PillButton>
      </div>
    </div>
  );
}
